import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import type { MessageData } from 'entities/Message'
import { textToHtml } from 'entities/Message/lib/textToHtml'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import type { MergeField } from 'entities/MergeField/model/MergeField'
import { htmlToText, mergeFieldReplacer } from 'widgets/MessageField'
import { type ITextToSpeechStoreProps } from 'widgets/TextToSpeech/store/textToSpeechWidgetStore'

export class TextToSpeechTextStore {
  readonly type: ITextToSpeechStoreProps['type']
  constructor(
    readonly entities: IEntitiesStore,
    { type }: { type: ITextToSpeechStoreProps['type'] }
  ) {
    this.type = type
    makeAutoObservable(this)
  }

  get isErrorLength() {
    return this.text.trim().length > 2160
  }
  get errorLength() {
    return this.isErrorLength && 'Your message must be under 3 minutes'
  }
  get error() {
    return this.validError || this.errorLength
  }

  setValidError = (error: string) => {
    this.validError = error
  }

  clearError = () => {
    this.validError = ''
  }

  private _messageHtml = ''
  get messageHtml() {
    return this._messageHtml
  }

  private _messageInnerText = ''
  get messageInnerText() {
    return this._messageInnerText
  }

  get text() {
    return htmlToText(this._messageHtml).trim()
  }

  validError = ''
  addContent: ((content: string) => void) | null = null
  setAddContentFn = (fn: typeof this.addContent) => {
    this.addContent = fn
  }
  private _replaceTriggerContent = ''
  private _updateTriggerContent = ''
  private _focusMessageFieldTrigger = ''
  private _activeMessageField = false

  setMessageText = (html: string, innerText: string) => {
    this._messageHtml = html
    this._messageInnerText = innerText
  }

  setUpdateTriggerContent = () => {
    this._updateTriggerContent = nanoid()
  }

  setActiveMessageField = (value: boolean) => {
    this._activeMessageField = value
  }

  setMessageData = ({ message, isReplace = false, isFocus = false }: MessageData) => {
    const newMessageHtml = textToHtml(this.entities, {
      text: message,
      extraSpace: true,
    })
    if (isReplace) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = newMessageHtml
      const extractedInnerText = tempDiv.innerText || tempDiv.textContent || message
      this.setMessageText(newMessageHtml, extractedInnerText)
    } else {
      this.addContent?.(newMessageHtml + '&nbsp;')
    }

    this._replaceTriggerContent = nanoid()
    if (isFocus) {
      this._focusMessageFieldTrigger = nanoid()
    }
  }

  get focusMessageFieldTrigger() {
    return this._focusMessageFieldTrigger
  }

  get activeMessageField() {
    return this._activeMessageField
  }

  get replaceTriggerContent() {
    return this._replaceTriggerContent
  }

  get updateTriggerContent() {
    return this._updateTriggerContent
  }

  handleMergeField = (field: MergeField) => {
    if (!this.addContent) return

    const fieldContent = mergeFieldReplacer(this.entities, field.template) + '&nbsp;'

    this.addContent(fieldContent)
    this.setUpdateTriggerContent()
  }
}
